<template>
  <div>
    <PageHeader :title="title" :items="items"/>
    <b-col class="text-end" sm="12">
      <remaining :used="used" :total="total"></remaining>
    </b-col>
    <div class="card">
      <div class="card-body">
        <b-row class="align-content-between justify-content-between">
          <b-col sm="12" md="8" xl="3" class="pt-1">
            <market-place-component @input="handleMarketplace"/>
          </b-col>

          <b-col sm="12" md="4" xl="9" class="pt-1 text-end">
            <b-btn @click="$router.push({ name: 'campaigns.create' })" variant="success" :disabled="disabledQuota">
              <i class="mdi mdi-tooltip-plus"></i>
              {{ $t('pages.campaign.index.new_campaign') }}
            </b-btn>
          </b-col>
        </b-row>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="col-sm-12">
          <div class="row">
            <div class="col-sm-6 col-12">
              <PerPageSelector v-model="query.perPage"
                               :available-per-page-options="availablePerPageOptions"
              ></PerPageSelector>
            </div>
            <div class="col-sm-3 offset-lg-3 offset-md-3 offset-sm-3 col-12">
              <DataTableSearch
                  v-model="query.filter"
                  :place-holder="$t('pages.campaign.index.search')"/>
            </div>
          </div>
        </div>

        <div class="col-sm-12">
          <div>
            <b-table
                ref="campaignTable"
                striped
                hover
                :empty-text="$t('components.bootstrap_table.empty_text')"
                :empty-filtered-text="$t('components.bootstrap_table.empty_filtered_text')"
                :fields="fields"
                :items="getCampaigns"
                :per-page="query.perPage"
                :sort-by="query.sortBy"
                :sort-desc="query.sortDesc"
                :current-page="query.page"
                show-empty
                :stacked="$store.state.app.isMobile"
                :filter="query.filter"
            >
              <template #empty>
                <no-result :title="$t('pages.campaign.index.first_campaign')"
                           :subtitle="$t('pages.campaign.index.help_first_campaign')"
                           icon="email-check-outline">
                  <template #btn>
                    <b-btn @click="$router.push({ name: 'campaigns.create' })" variant="light" :disabled="disabledQuota">
                      <i class="mdi mdi-tooltip-plus"></i>
                      {{ $t('pages.campaign.index.new_campaign') }}
                    </b-btn>
                  </template>
                </no-result>
              </template>

              <template v-slot:cell(stats)="data">
                <div>
                  <div class="d-flex text-muted">
                    {{ $t('pages.campaign.index.stats.sent') }}:
                    <h6 class="badge-stat">
                      <b-badge class="mx-1" variant="success">
                        {{ data.item.stats.sent }}
                      </b-badge>
                    </h6>
                  </div>
                  <div class="d-flex text-muted">
                    {{ $t('pages.campaign.index.stats.queue') }}:
                    <h6 class="badge-stat">
                      <b-badge class="mx-1" variant="info">
                        {{ data.item.stats.queue }}
                      </b-badge>
                    </h6>
                  </div>
                  <div class="d-flex text-muted">
                    {{ $t('pages.campaign.index.stats.canceled') }}:
                    <h6 class="badge-stat">
                      <b-badge class="mx-1" variant="danger">
                        {{ data.item.stats.cancel }}
                      </b-badge>
                    </h6>
                  </div>
                </div>
              </template>

              <template v-slot:cell(template)="data">
                <span v-if="data.item.template.type === 'productReviewAndSellerFeedback'">
                  <i class="mdi mdi-amazon"></i>
                  {{ data.item.template.name }}
                </span>
                <a @click="handleTemplateClick(data.item)"
                   v-else
                   href="javascript: void(0);">
                  <i class="mdi mdi-open-in-new"></i>
                  {{ data.item.template.name }}
                </a>
              </template>

              <template v-slot:cell(status)="data">
                <div class="form-check form-switch form-switch-md mb-3">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="data.item.status"
                      @input="updateStatus(data.item.id, !data.item.status)"
                  />
                </div>
              </template>

              <template v-slot:cell(action)="data">
                <b-dropdown
                    class="card-drop"
                    variant="white"
                    right
                    toggle-class="p-0"
                    menu-class="dropdown-menu-end"
                >
                  <template v-slot:button-content>
                    <i class="mdi mdi-dots-horizontal font-size-18"></i>
                  </template>

                  <b-dropdown-item @click="$router.push({name: 'campaign.edit', params: {campaignId: data.item.id}})">
                    <i class="fas fa-pencil-alt text-success me-1"></i>
                    {{ $t('pages.campaign.index.edit') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click="deleteCampaign(data.item)">
                    <i class="fas fa-trash-alt text-danger me-1"></i>
                    {{ $t('pages.campaign.index.delete') }}
                  </b-dropdown-item>
                </b-dropdown>
              </template>

            </b-table>
            <div class="row">
              <div class="col-sm">
                <Pagination :meta="campaigns.meta"></Pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import queryBuilder from "../../../mixins/queryBuilder";
import CampaignService from "../../../Services/Api/CampaignService";
import Swal from "sweetalert2";
import PerPageSelector from "../../../components/PerPageSelector";
import MarketPlaceComponent from "../../../components/widgets/MarketPlaceComponent";
import Remaining from "../../../components/Remaining";
import QuotaMixin from "../../../mixins/QuotaMixin";
import NoResult from "../../../components/widgets/NoResult";
export default {
  name: "Campaign",
  components: {
    NoResult,
    Remaining,
    PerPageSelector,
    MarketPlaceComponent
  },
  page() {
    return {
      title: this.$t('pages.campaign.index.title'),
      meta: [
        {
          name: "description",
          content: this.$t('common.app_title')
        }
      ]
    }
  },
  mixins: [queryBuilder, QuotaMixin],
  data() {
    return {
      fields: [
        {key: 'name', sortable: true, label: this.$t('pages.campaign.index.fields.name')},
        {key: 'stats', sortable: false, label: this.$t('pages.campaign.index.fields.stats')},
        {key: 'template', sortable: false, label: this.$t('pages.campaign.index.fields.template')},
        {key: 'status', sortable: false, label: this.$t('pages.campaign.index.fields.status')},
        {key: 'created_at', sortable: true, label: this.$t('pages.campaign.index.fields.created_at')},
        {key: 'action', sortable: false, label: this.$t('pages.campaign.index.fields.action')},
      ],
      items: [
        {
          text: this.$t('pages.campaign.index.items.first'),
          href: "/",
        },
        {
          text: this.$t('pages.campaign.index.items.second'),
          active: true,
        },
      ],
      title: this.$t('pages.campaign.index.title'),
      campaigns: {},
      quotaType: 'campaigns'
    }
  },
  methods: {
    async updateStatus(id, status) {
      await CampaignService.statusUpdate({id, payload: {status}})
    },
    handleMarketplace(e) {
      this.query.marketplace = e.map(item => item.marketplace_id).join(',')
      this.$refs.campaignTable.refresh()
    },
    async getCampaigns(params = this.query) {
      this.query = {...this.query, ...params}
      const query = this.buildQuery()
      const {data: campaigns} = await CampaignService.all({query})
      this.campaigns = campaigns
      return campaigns.data
    },
    handleTemplateClick(item) {
      if (item.template.type !== 'productReviewAndSellerFeedback') {
        this.$helpers.openNewTab('email.design', {emailID: item.template.id})
      }
    },
    deleteCampaign(item) {
      Swal.fire({
        title: this.$t('pages.campaign.index.delete_title', {name: item.name}),
        icon: "error",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonColor: "#f46a6a",
        cancelButtonColor: "#34c38f",
        confirmButtonText: this.$t('pages.campaign.index.delete')
      }).then(result => {
        if (result.value) {
          CampaignService.destroy({id: item.id}).then(() => {
            this.$store.dispatch('quota/getSubscriptionQuota')
            this.$refs.campaignTable.refresh()
          })
        }
      });
    }
  }
}
</script>

<style scoped>
.badge-stat {
  margin-top: 2px;
}
</style>